import axiosInstance from "@/utils/axiosInstance";

export const TENANT_GOVERNANCE_SEVERITY = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  CRITICAL: "critical",
  UNKNOWN: "unknown",
  INFORMATIONAL: "informational",
} as const;

export const TENANT_GOVERNANCE_READINESS = {
  PILOT_OBSERVE: "pilot-observe",
  HIGH_SENSITIVITY: "high-sensitivity",
  TENANT_APPROVAL_NEEDED: "tenant-approval-needed",
  SERVICE_OWNED: "service-owned",
  CONCEPTUAL: "conceptual",
  EXTERNAL_ROLLOUT_GATED: "external-rollout-gated",
} as const;

export const TENANT_GOVERNANCE_LAYER = {
  RUNTIME_FLAGS: "Runtime Flags",
  WORKFLOW_TOPOLOGY: "Workflow Topology",
  WORKFLOW_LEGALITY: "Workflow Legality",
  GOVERNANCE_POLICY: "Governance Policy",
  ORCHESTRATION_POLICY: "Orchestration Policy",
  OVERSIGHT_POLICY: "Oversight Policy",
  DATA_EXPOSURE_POLICY: "Data Exposure Policy",
  MENTORSHIP_POLICY: "Mentorship Policy",
} as const;

export type TenantGovernanceSeverity =
  (typeof TENANT_GOVERNANCE_SEVERITY)[keyof typeof TENANT_GOVERNANCE_SEVERITY];

export type TenantGovernanceReadiness =
  (typeof TENANT_GOVERNANCE_READINESS)[keyof typeof TENANT_GOVERNANCE_READINESS];

export type TenantGovernanceLayer =
  (typeof TENANT_GOVERNANCE_LAYER)[keyof typeof TENANT_GOVERNANCE_LAYER];

export interface TenantGovernanceSettingsProjection {
  allow_shared_phone: boolean;
  allow_multiple_disbursals: boolean;
  disbursal_policy_scope: string;
  disbursal_max_count?: number | null;
  disbursal_max_amount?: number | null;
}

export interface TenantSettingsUpdatePayload {
  allow_shared_phone?: boolean;
  allow_multiple_disbursals?: boolean;
  disbursal_policy_scope?: "lifetime" | "academic_year";
  disbursal_max_count?: number | null;
  disbursal_max_amount?: number | null;
  change_reason: string;
}

export interface TenantWorkflowConfigProjection {
  id: number;
  program_code: string;
  academic_year: string;
  version: number;
  is_active: boolean;
  requires_interview: boolean;
  requires_mentoring: boolean;
  requires_management_approval: boolean;
  requires_client_governance: boolean;
  document_requirement_count: number;
  transition_count: number;
}

export interface ReviewProcessReadinessProjection {
  program_code: string;
  academic_year: string;
  status:
    | "not_selected"
    | "selected"
    | "provisioned"
    | "ready_for_launch"
    | "mismatch"
    | string;
  selected_workflow_template_id?: number | null;
  selected_review_process_name?: string | null;
  provisioned_workflow_config_id?: number | null;
  provisioned_workflow_config_version?: number | null;
  provisioned_workflow_template_id?: number | null;
  provisioned_review_process_name?: string | null;
  transition_count: number;
  active_form_version_count: number;
  mismatch: boolean;
  message: string;
}

export interface GovernanceAuthorityProjection {
  layer: string;
  current_authority: string;
  future_authority: string;
  status: string;
}

export interface GovernanceDriftWarningProjection {
  warning_id: string;
  severity: TenantGovernanceSeverity;
  layer: TenantGovernanceLayer;
  title: string;
  description: string;
  recommended_action: string;
  active_runtime: boolean;
}

export interface GovernanceChangeHistoryProjection {
  audit_id: number;
  timestamp?: string | null;
  actor_id?: number | null;
  actor_role?: string | null;
  action: string;
  reason?: string | null;
  changed_fields: string[];
  before: Record<string, unknown>;
  after: Record<string, unknown>;
  governance_layer?: TenantGovernanceLayer | null;
  severity?: TenantGovernanceSeverity | null;
}

export interface CandidatePolicyDomainProjection {
  domain: string;
  current_authority: string;
  future_authority: string;
  readiness: TenantGovernanceReadiness;
  rationale: string;
  recommended_next_step: string;
  evidence_count: number;
}

export interface GovernanceMutabilityProjection {
  layer: string;
  mutability_level: string;
  doctrine: string;
}

export interface GovernanceActivationRuleProjection {
  required_sequence: string[];
  forbidden_sequence: string[];
  rationale: string;
}

export interface GovernanceChangeSeverityProjection {
  change_type: string;
  severity: string;
  rationale: string;
}

export interface GovernanceProvenanceRequirementProjection {
  requirement: string;
  example: string;
}

export interface TenantGovernancePolicyProjection {
  tenant_id: number;
  tenant_slug: string;
  tenant_name: string;
  read_only: boolean;
  classification: string;
  doctrine_principles: string[];
  settings: TenantGovernanceSettingsProjection;
  workflow_configs: TenantWorkflowConfigProjection[];
  review_process_readiness: ReviewProcessReadinessProjection[];
  authority_map: GovernanceAuthorityProjection[];
  drift_warnings: GovernanceDriftWarningProjection[];
  change_history: GovernanceChangeHistoryProjection[];
  normalization_guidance: string[];
  candidate_policy_domains: string[];
  candidate_policy_domain_details: CandidatePolicyDomainProjection[];
  mutability_model: GovernanceMutabilityProjection[];
  activation_rule: GovernanceActivationRuleProjection;
  change_severity_model: GovernanceChangeSeverityProjection[];
  provenance_requirements: GovernanceProvenanceRequirementProjection[];
  metadata: {
    active_workflow_configs?: number;
    active_workflow_configs_with_transitions?: number;
    candidate_policy_readiness_counts?: Partial<
      Record<TenantGovernanceReadiness, number>
    >;
    change_history_count?: number;
    change_severity_counts?: Partial<Record<TenantGovernanceSeverity, number>>;
    drift_warning_count?: number;
    highest_recent_change_severity?: TenantGovernanceSeverity;
    incomplete_change_provenance_count?: number;
    projection_source?: string;
  };
}

export async function fetchTenantGovernancePolicy(
  tenantId: number
): Promise<TenantGovernancePolicyProjection> {
  const res = await axiosInstance.get<TenantGovernancePolicyProjection>(
    `/tenants/${tenantId}/governance-policy`
  );
  return res.data;
}

export async function updateTenantRuntimeSettings(
  tenantId: number,
  payload: TenantSettingsUpdatePayload
): Promise<TenantGovernanceSettingsProjection> {
  const res = await axiosInstance.put<TenantGovernanceSettingsProjection>(
    `/tenants/${tenantId}/settings`,
    payload
  );
  return res.data;
}
